import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Eye, Package, User, MapPin, Phone, Trash2, AlertTriangle } from 'lucide-react';

const statusColors: Record<string, string> = {
  pending: 'bg-warning/10 text-warning border-warning/30',
  confirmed: 'bg-sky/10 text-sky border-sky/30',
  shipped: 'bg-primary/10 text-primary border-primary/30',
  delivered: 'bg-success/10 text-success border-success/30',
  cancelled: 'bg-destructive/10 text-destructive border-destructive/30',
};

const AdminOrders = () => {
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  const { data: orders = [] } = useQuery({
    queryKey: ['admin', 'orders'],
    enabled: !!user && isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_name,
            price,
            quantity,
            product_id
          )
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('orders').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast({ title: 'Order status updated' });
    },
  });

  const deleteOrder = useMutation({
    mutationFn: async (orderId: string) => {
      // First delete order items (foreign key constraint)
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);
      
      if (itemsError) throw itemsError;

      // Then delete the order
      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);
      
      if (orderError) throw orderError;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast({ 
        title: 'Order deleted', 
        description: 'The order has been permanently removed from the system.' 
      });
    },
    onError: (error) => {
      toast({ 
        title: 'Delete failed', 
        description: error instanceof Error ? error.message : 'Failed to delete order',
        variant: 'destructive' 
      });
    },
  });

  const bulkDeleteDelivered = useMutation({
    mutationFn: async () => {
      // Get all delivered order IDs
      const { data: deliveredOrders, error: fetchError } = await supabase
        .from('orders')
        .select('id')
        .eq('status', 'delivered');
      
      if (fetchError) throw fetchError;
      if (!deliveredOrders || deliveredOrders.length === 0) {
        throw new Error('No delivered orders to delete');
      }

      const orderIds = deliveredOrders.map(o => o.id);

      // Delete order items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .in('order_id', orderIds);
      
      if (itemsError) throw itemsError;

      // Delete orders
      const { error: ordersError } = await supabase
        .from('orders')
        .delete()
        .eq('status', 'delivered');
      
      if (ordersError) throw ordersError;

      return deliveredOrders.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast({ 
        title: 'Bulk delete successful', 
        description: `${count} delivered order${count !== 1 ? 's' : ''} have been removed.` 
      });
    },
    onError: (error) => {
      toast({ 
        title: 'Bulk delete failed', 
        description: error instanceof Error ? error.message : 'Failed to delete orders',
        variant: 'destructive' 
      });
    },
  });

  const deleteAllOrders = useMutation({
    mutationFn: async () => {
      // Get all order IDs
      const { data: allOrders, error: fetchError } = await supabase
        .from('orders')
        .select('id');
      
      if (fetchError) throw fetchError;
      if (!allOrders || allOrders.length === 0) {
        throw new Error('No orders to delete');
      }

      const orderIds = allOrders.map(o => o.id);

      // Delete all order items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .in('order_id', orderIds);
      
      if (itemsError) throw itemsError;

      // Delete all orders
      const { error: ordersError } = await supabase
        .from('orders')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (using a condition that matches all)
      
      if (ordersError) throw ordersError;

      return allOrders.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast({ 
        title: 'All orders deleted', 
        description: `${count} order${count !== 1 ? 's' : ''} have been permanently removed from the system.` 
      });
    },
    onError: (error) => {
      toast({ 
        title: 'Delete all failed', 
        description: error instanceof Error ? error.message : 'Failed to delete all orders',
        variant: 'destructive' 
      });
    },
  });

  const deleteSelectedOrders = useMutation({
    mutationFn: async (orderIds: string[]) => {
      if (orderIds.length === 0) {
        throw new Error('No orders selected');
      }

      // Delete order items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .in('order_id', orderIds);
      
      if (itemsError) throw itemsError;

      // Delete selected orders
      const { error: ordersError } = await supabase
        .from('orders')
        .delete()
        .in('id', orderIds);
      
      if (ordersError) throw ordersError;

      return orderIds.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      setSelectedOrderIds([]);
      toast({ 
        title: 'Selected orders deleted', 
        description: `${count} order${count !== 1 ? 's' : ''} have been removed.` 
      });
    },
    onError: (error) => {
      toast({ 
        title: 'Delete selected failed', 
        description: error instanceof Error ? error.message : 'Failed to delete selected orders',
        variant: 'destructive' 
      });
    },
  });

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map((o: any) => o.id));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-2xl font-bold">Orders ({orders.length})</h1>
          {selectedOrderIds.length > 0 && (
            <Badge variant="secondary" className="text-sm">
              {selectedOrderIds.length} selected
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Delete Selected Orders Button */}
          {selectedOrderIds.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete Selected ({selectedOrderIds.length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Delete Selected Orders?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      This will permanently delete <strong>{selectedOrderIds.length} selected order(s)</strong> from the database.
                    </p>
                    <p className="text-destructive font-medium">
                      ⚠️ This action cannot be undone!
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteSelectedOrders.mutate(selectedOrderIds)}
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={deleteSelectedOrders.isPending}
                  >
                    {deleteSelectedOrders.isPending ? 'Deleting...' : `Delete ${selectedOrderIds.length} Order${selectedOrderIds.length !== 1 ? 's' : ''}`}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Delete All Delivered Orders Button */}
          {orders.filter((o: any) => o.status === 'delivered').length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                  <Trash2 className="h-4 w-4" />
                  Delete Delivered ({orders.filter((o: any) => o.status === 'delivered').length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Delete All Delivered Orders?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      This will permanently delete <strong>{orders.filter((o: any) => o.status === 'delivered').length} delivered order(s)</strong> from the database.
                    </p>
                    <p className="text-destructive font-medium">
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="text-sm">
                      Only orders with "delivered" status will be removed. All other orders will remain intact.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => bulkDeleteDelivered.mutate()}
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={bulkDeleteDelivered.isPending}
                  >
                    {bulkDeleteDelivered.isPending ? 'Deleting...' : 'Delete All Delivered'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Delete ALL Orders Button */}
          {orders.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete All ({orders.length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Delete ALL Orders?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p className="text-base">
                      This will permanently delete <strong className="text-destructive">ALL {orders.length} order(s)</strong> from the database, regardless of their status.
                    </p>
                    <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg space-y-1">
                      <p className="font-semibold text-destructive">⚠️ DANGER ZONE</p>
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        <li>All pending orders will be deleted</li>
                        <li>All confirmed orders will be deleted</li>
                        <li>All shipped orders will be deleted</li>
                        <li>All delivered orders will be deleted</li>
                        <li>All cancelled orders will be deleted</li>
                      </ul>
                    </div>
                    <p className="text-destructive font-bold text-base">
                      🚨 THIS ACTION CANNOT BE UNDONE!
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This will also allow you to delete products that have existing orders.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteAllOrders.mutate()}
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={deleteAllOrders.isPending}
                  >
                    {deleteAllOrders.isPending ? 'Deleting All...' : 'Yes, Delete Everything'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={orders.length > 0 && selectedOrderIds.length === orders.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all orders"
                />
              </TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No orders yet</TableCell></TableRow>
            ) : orders.map((order: any) => {
              const address = order.shipping_address || {};
              const itemCount = order.order_items?.length || 0;
              
              return (
                <TableRow key={order.id} className={selectedOrderIds.includes(order.id) ? 'bg-muted/50' : ''}>
                  <TableCell>
                    <Checkbox
                      checked={selectedOrderIds.includes(order.id)}
                      onCheckedChange={() => toggleOrderSelection(order.id)}
                      aria-label={`Select order ${order.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{order.id.slice(0, 8)}...</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-sm">{address.name || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{order.phone || address.phone || 'N/A'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{format(new Date(order.created_at), 'dd MMM yyyy')}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Package className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">₹{Number(order.total).toLocaleString('en-IN')}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${statusColors[order.status] || ''}`}>
                      {order.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="font-heading">Order Details</DialogTitle>
                          </DialogHeader>
                          
                          {selectedOrder && (
                            <div className="space-y-6">
                              {/* Order Info */}
                              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                                <div>
                                  <p className="text-sm font-medium">Order ID</p>
                                  <p className="font-mono text-xs">{selectedOrder.id}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">Date</p>
                                  <p className="text-sm">{format(new Date(selectedOrder.created_at), 'dd MMM yyyy, HH:mm')}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">Status</p>
                                  <Badge className={`text-xs ${statusColors[selectedOrder.status] || ''}`}>
                                    {selectedOrder.status}
                                  </Badge>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">Payment</p>
                                  <p className="text-sm capitalize">{selectedOrder.payment_method}</p>
                                </div>
                              </div>

                              {/* Customer Details */}
                              <div className="space-y-3">
                                <h3 className="font-heading font-semibold flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  Customer Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                                  <div>
                                    <p className="text-sm font-medium">Full Name</p>
                                    <p className="text-sm">{selectedOrder.shipping_address?.name || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      Phone Number
                                    </p>
                                    <p className="text-sm">{selectedOrder.phone || selectedOrder.shipping_address?.phone || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Delivery Address */}
                              <div className="space-y-3">
                                <h3 className="font-heading font-semibold flex items-center gap-2">
                                  <MapPin className="h-4 w-4" />
                                  Delivery Address
                                </h3>
                                <div className="p-4 border rounded-lg">
                                  {selectedOrder.shipping_address ? (
                                    <div className="space-y-2">
                                      <p className="text-sm">{selectedOrder.shipping_address.address}</p>
                                      <p className="text-sm">
                                        {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">No address information available</p>
                                  )}
                                </div>
                              </div>

                              {/* Order Items */}
                              <div className="space-y-3">
                                <h3 className="font-heading font-semibold flex items-center gap-2">
                                  <Package className="h-4 w-4" />
                                  Ordered Products
                                </h3>
                                <div className="border rounded-lg overflow-hidden">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Price</TableHead>
                                        <TableHead>Qty</TableHead>
                                        <TableHead>Total</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {selectedOrder.order_items?.map((item: any) => (
                                        <TableRow key={item.id}>
                                          <TableCell className="font-medium">{item.product_name}</TableCell>
                                          <TableCell>₹{Number(item.price).toLocaleString('en-IN')}</TableCell>
                                          <TableCell>{item.quantity}</TableCell>
                                          <TableCell>₹{(Number(item.price) * item.quantity).toLocaleString('en-IN')}</TableCell>
                                        </TableRow>
                                      )) || (
                                        <TableRow>
                                          <TableCell colSpan={4} className="text-center text-muted-foreground">No items found</TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                                <div className="flex justify-end p-4 bg-muted/50 rounded-lg">
                                  <div className="text-right">
                                    <p className="font-heading font-bold text-lg">
                                      Total: ₹{Number(selectedOrder.total).toLocaleString('en-IN')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <Select 
                        value={order.status} 
                        onValueChange={status => updateStatus.mutate({ id: order.id, status })}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map(s => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Delete Button - Only for delivered orders */}
                      {order.status === 'delivered' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-destructive" />
                                Delete Delivered Order?
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-2">
                                <p>
                                  Are you sure you want to delete this delivered order?
                                </p>
                                <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                                  <p><strong>Order ID:</strong> {order.id.slice(0, 8)}...</p>
                                  <p><strong>Customer:</strong> {address.name || 'N/A'}</p>
                                  <p><strong>Total:</strong> ₹{Number(order.total).toLocaleString('en-IN')}</p>
                                </div>
                                <p className="text-destructive font-medium">
                                  ⚠️ This action cannot be undone!
                                </p>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteOrder.mutate(order.id)}
                                className="bg-destructive hover:bg-destructive/90"
                                disabled={deleteOrder.isPending}
                              >
                                {deleteOrder.isPending ? 'Deleting...' : 'Delete Order'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminOrders;
